import { zodResolver } from '@hookform/resolvers/zod'
import { CreateInviteInput, Role, type InviteStatus } from '@numistic/contracts'
import {
  useAdminInvites,
  useAdminSettings,
  useCreateInvite,
  useRevokeInvite,
  useUpdateAdminSettings,
} from '@numistic/data'
import { useTranslation } from '@numistic/i18n'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  Input,
  Select,
  Table,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from '@numistic/ui'
import { createRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { copyText } from '../../lib/clipboard.js'
import { MutationErrors } from '../../components/mutation-errors.js'
import { useErrorText } from '../../lib/errors.js'
import { formatDate } from '../../lib/format.js'
import { adminRoute } from './layout.js'

export const adminInvitesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/invites',
  component: AdminInvitesPage,
})

const STATUS_VARIANT: Record<InviteStatus, 'success' | 'muted' | 'destructive' | 'default'> = {
  valid: 'success',
  used: 'default',
  expired: 'muted',
  revoked: 'destructive',
}

function AdminInvitesPage() {
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,22rem)_1fr] lg:items-start">
      <div className="grid gap-6">
        <RegistrationSettings />
        <CreateInvite />
      </div>
      <InvitesTable />
    </div>
  )
}

function RegistrationSettings() {
  const { t } = useTranslation()
  const settings = useAdminSettings()
  const update = useUpdateAdminSettings()
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.settingsTitle')}</CardTitle>
        {settings.data ? (
          <CardDescription>
            {settings.data.mailConfigured ? t('admin.mailOn') : t('admin.mailOff')}
          </CardDescription>
        ) : null}
      </CardHeader>
      <CardContent>
        <Field label={t('admin.registrationMode')}>
          <Select
            value={settings.data?.registrationMode ?? 'invite'}
            disabled={!settings.data || update.isPending}
            onChange={(e) =>
              update.mutate({ registrationMode: e.target.value as 'invite' | 'open' })
            }
          >
            <option value="invite">{t('admin.modeInvite')}</option>
            <option value="open">{t('admin.modeOpen')}</option>
          </Select>
        </Field>
        <MutationErrors errors={[update.error]} />
      </CardContent>
    </Card>
  )
}

const FormSchema = z.object({
  email: z.union([z.literal(''), CreateInviteInput.shape.email.unwrap()]),
  role: Role,
  ttlDays: z.coerce.number<string>().int().min(1).max(90),
})

function CreateInvite() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const create = useCreateInvite()
  const [copied, setCopied] = useState(false)
  const form = useForm({
    resolver: zodResolver(FormSchema),
    defaultValues: { email: '', role: 'user' as Role, ttlDays: '7' },
  })

  const onSubmit = form.handleSubmit(({ email, role, ttlDays }) => {
    setCopied(false)
    create.mutate({ email: email || undefined, role, ttlDays }, { onSuccess: () => form.reset() })
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.createInvite')}</CardTitle>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={onSubmit} noValidate>
          {create.error ? (
            <Alert variant="destructive">{errorText.request(create.error)}</Alert>
          ) : null}
          <Field
            label={t('admin.inviteEmail')}
            hint={t('admin.inviteEmailHint')}
            error={errorText.field(form.formState.errors.email)}
          >
            <Input type="email" {...form.register('email')} />
          </Field>
          <div className="grid grid-cols-[1fr_6rem] gap-3">
            <Field label={t('admin.inviteRole')}>
              <Select {...form.register('role')}>
                {Role.options.map((r) => (
                  <option key={r} value={r}>
                    {t(`roles.${r}`)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field
              label={t('admin.inviteTtl')}
              error={errorText.field(form.formState.errors.ttlDays)}
            >
              <Input type="number" min={1} max={90} {...form.register('ttlDays')} />
            </Field>
          </div>
          <Button type="submit" disabled={create.isPending}>
            {t('admin.createInvite')}
          </Button>
          {create.data ? (
            <Alert variant="success" className="grid gap-2">
              <span>{t('admin.inviteCreated')}</span>
              <code
                className="break-all rounded bg-card px-2 py-1 text-xs"
                data-testid="invite-url"
              >
                {create.data.url}
              </code>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void copyText(create.data.url).then(setCopied)}
              >
                {copied ? t('admin.copied') : t('admin.copy')}
              </Button>
            </Alert>
          ) : null}
        </form>
      </CardContent>
    </Card>
  )
}

function InvitesTable() {
  const { t } = useTranslation()
  const errorText = useErrorText()
  const invites = useAdminInvites()
  const revoke = useRevokeInvite()
  if (invites.error) return <Alert variant="destructive">{errorText.request(invites.error)}</Alert>
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('admin.invitesTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <THead>
            <Tr>
              <Th>{t('admin.columns.email')}</Th>
              <Th>{t('admin.columns.role')}</Th>
              <Th>{t('admin.columns.status')}</Th>
              <Th>{t('admin.columns.expires')}</Th>
              <Th>{t('admin.columns.usedBy')}</Th>
              <Th />
            </Tr>
          </THead>
          <TBody>
            {invites.data?.map((inv) => (
              <Tr key={inv.id}>
                <Td>
                  {inv.email ?? (
                    <span className="text-muted-foreground">{t('admin.anyEmail')}</span>
                  )}
                </Td>
                <Td>{t(`roles.${inv.role}`)}</Td>
                <Td>
                  <Badge variant={STATUS_VARIANT[inv.status]}>
                    {t(`admin.inviteStatus.${inv.status}`)}
                  </Badge>
                </Td>
                <Td className="text-muted-foreground">{formatDate(inv.expiresAt)}</Td>
                <Td className="text-muted-foreground">{inv.usedByEmail ?? '—'}</Td>
                <Td className="text-right">
                  {inv.status === 'valid' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={revoke.isPending}
                      onClick={() => revoke.mutate(inv.id)}
                    >
                      {t('admin.revoke')}
                    </Button>
                  ) : null}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
        <MutationErrors errors={[revoke.error]} />
      </CardContent>
    </Card>
  )
}
